import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Calendar as BigCalendar, momentLocalizer, View, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CalendarEvent, EVENT_TYPE_COLORS } from '../types/calendar';
import { CalendarService } from '../services/calendarService';
import { useAuth } from '../contexts/AuthContext';
import { EventModal } from '../components/EventModal';
import { EventForm } from '../components/EventForm';
import { CalendarToolbar } from '../components/CalendarToolbar';
import { Calendar as CalendarIcon } from 'lucide-react';
import './Calendar.css';

const localizer = momentLocalizer(moment);

export interface CalendarRef {
    // Azure/Storage status
    azureConnected: boolean;
    azureStatus: string;
    isRefreshing: boolean;
    handleRefreshFromAzure: () => void;     

    // Event management
    handleAddEvent: () => void;
}

export const Calendar = forwardRef<CalendarRef>((props, ref) => {
    const { isAuthenticated, isCalendarEditor, graphClient, isBypassMode } = useAuth();
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showEventForm, setShowEventForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
    const [view, setView] = useState<View>(Views.MONTH);
    const [date, setDate] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [azureConnected, setAzureConnected] = useState(false);
    const [originalEvents, setOriginalEvents] = useState<CalendarEvent[]>([]);
    const [azureStatus, setAzureStatus] = useState<string>('checking');
    const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date; end: Date } | null>(null);    
    const [customColors, setCustomColors] = useState<Record<string, string>>({});

    const loadEvents = () => {
        const loadedEvents = CalendarService.getEvents();
        setEvents(loadedEvents);
        setOriginalEvents(loadedEvents);
        setAzureConnected(CalendarService.isAzureBlobAvailable());
    };

    const loadEventsAsync = useCallback(async () => {
        try {
            if (azureConnected) {
                setAzureStatus('loading');
            }
            const loadedEvents = await CalendarService.getEventsAsync();
            setEvents(loadedEvents);
            setOriginalEvents(loadedEvents);
            if (azureConnected) {
                setAzureStatus('connected');
            }
        } catch (error) {
            console.error('Error loading events asynchronously:', error);
            if (azureConnected) {
                setAzureStatus('error');
            }
        }
    }, [azureConnected]);

    useEffect(() => {
        loadEvents();
        loadEventsAsync();
    }, [loadEventsAsync]);

    const handleRefreshFromAzure = async () => {
        if (!azureConnected) return;

        setIsRefreshing(true);
        try {
            const refreshedEvents = await CalendarService.refreshFromAzure();
            setEvents(refreshedEvents);
            setOriginalEvents(refreshedEvents);
        } catch (error) {
            console.error('Error refreshing from Azure:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    

    const handleAddEvent = () => {
        setEditingEvent(null);
        setSelectedDateRange(null);
        setShowEventForm(true);
    };

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
        azureConnected,
        azureStatus,
        isRefreshing,
        handleRefreshFromAzure, 
        handleAddEvent
    }));

    const handleSelectEvent = (event: CalendarEvent) => {
        setSelectedEvent(event);
        setShowEventModal(true);
    };

    const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
        if (isCalendarEditor) {
            // React-big-calendar adds 1 day to the end date for all-day events
            // We need to subtract 1 day to get the actual selected end date
            const adjustedEnd = new Date(end);
            adjustedEnd.setDate(adjustedEnd.getDate() - 1);

            setSelectedDateRange({ start, end: adjustedEnd });
            setEditingEvent(null);
            setShowEventForm(true);
        }
    };

    const handleCreateEvent = (eventData: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>) => {
        CalendarService.addEvent(eventData);
        const updatedEvents = CalendarService.getEvents();
        setEvents(updatedEvents);
        setOriginalEvents(updatedEvents);
        setShowEventForm(false);
        setSelectedDateRange(null);
    };

    const handleUpdateEvent = (id: string, updates: Partial<CalendarEvent>) => {
        CalendarService.updateEvent(id, updates);
        const updatedEvents = CalendarService.getEvents();
        setEvents(updatedEvents);
        setOriginalEvents(updatedEvents);
        setShowEventForm(false);
        setEditingEvent(null);
    };

    const handleDeleteEvent = (id: string) => {
        CalendarService.deleteEvent(id);
        const updatedEvents = CalendarService.getEvents();
        setEvents(updatedEvents);
        setOriginalEvents(updatedEvents);
        setShowEventModal(false);
        setSelectedEvent(null);
    };

    const handleEditEvent = (event: CalendarEvent) => {
        setEditingEvent(event);
        setShowEventForm(true);
        setShowEventModal(false);
    };

    const handleAddToOutlook = async (event: CalendarEvent) => {
        if (isBypassMode) {
            alert('Adding to personal calendar is disabled in demo mode.');
            return;
        }

        if (!graphClient) {
            alert('Unable to connect to Outlook. Please ensure you are logged in.');
            return;
        }

        try {
            const success = await CalendarService.addToOutlookCalendar(event, graphClient);
            if (success) {
                alert('Event added to your Outlook calendar successfully!');
            } else {
                alert('Failed to add event to Outlook calendar.');
            }
        } catch (error) {
            console.error('Error adding to Outlook:', error);
            alert('Failed to add event to Outlook calendar.');
        }
    };

    const eventStyleGetter = (event: CalendarEvent) => {
        const eventType = event.eventType || 'OTHER';
        // Use custom color if available, otherwise fall back to default
        const eventTypeColor = customColors[eventType] || EVENT_TYPE_COLORS[eventType as keyof typeof EVENT_TYPE_COLORS] || EVENT_TYPE_COLORS.OTHER;
        const isSynced = event.isSynced || false;

        return {
            style: {
                backgroundColor: eventTypeColor,
                borderColor: eventTypeColor,
                borderWidth: '1px',
                borderStyle: isSynced ? 'dashed' : 'solid',
                color: 'white',
                fontWeight: 'normal',
                opacity: isSynced ? 0.8 : 1,
            },
        };
    };

    const handleColorChange = (newCustomColors: Record<string, string>) => {
        setCustomColors(newCustomColors);
    };

    if (!isAuthenticated) {
        return (
            <div className="calendar-container">
                <div className="auth-prompt">
                    <CalendarIcon size={48} />
                    <h2>Please log in to view the company calendar</h2>
                    <p>You need to authenticate with your Microsoft work account to access the calendar.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="calendar-container">
            <CalendarToolbar
                events={events}
                onFilterChange={setEvents}
                originalEvents={originalEvents}
                onColorChange={handleColorChange}
            />

            <div className="calendar-wrapper">
                <BigCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '750px' }}
                    onSelectEvent={handleSelectEvent}
                    onSelectSlot={handleSelectSlot}
                    selectable={isCalendarEditor}
                    eventPropGetter={eventStyleGetter}
                    view={view}
                    onView={setView}
                    date={date}
                    onNavigate={setDate}
                    views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                    popup
                    tooltipAccessor={(event: CalendarEvent) => {
                        const syncInfo = event.isSynced ? `\nSource: ${event.syncSource || 'External'}` : '';
                        const readOnlyInfo = event.isSynced ? '\n(Read-only)' : '';
                        const eventTypeInfo = event.eventType ? `\nType: ${event.eventType}` : '';
                        return `${event.title}\n${event.description}${eventTypeInfo}${syncInfo}${readOnlyInfo}`;
                    }}
                />
            </div>

            {showEventModal && selectedEvent && (
                <EventModal
                    event={selectedEvent}
                    onClose={() => {
                        setShowEventModal(false);
                        setSelectedEvent(null);
                    }}
                    onEdit={handleEditEvent}
                    onDelete={handleDeleteEvent}
                    onAddToOutlook={handleAddToOutlook}
                    canEdit={isCalendarEditor}
                />
            )}

            {showEventForm && (
                <EventForm
                    event={editingEvent}
                    initialDateRange={selectedDateRange}
                    onSave={editingEvent ?
                        (updates) => handleUpdateEvent(editingEvent.id, updates) :
                        handleCreateEvent
                    }
                    onCancel={() => {
                        setShowEventForm(false);
                        setEditingEvent(null);
                        setSelectedDateRange(null);
                    }}
                />
            )}
        </div>
    );
});